import React, { lazy, Suspense, Component } from 'react';
import PropTypes from 'prop-types';

import { noop } from 'lodash';
import { LocaleService, ConfigsService } from '@sm360/widget-commons-core-services';
import Logger from '@sm360/logger-sentry';
import { NoSSR, Account, LoaderFullScreen, ProfileService } from '@sm360/widget-commons-general';
import { Wizard } from '@sm360/widget-commons-forms';
import { Vehicle, Quote } from '@sm360/widget-commons-vehicle';
import { StepBar, ProgressBar } from '@sm360/ui-library';

import TradeInModel from 'trade-in/model/TradeInModel.js';
import TradeInModelNew from 'trade-in/model/TradeInModelNew.js';
import TradeInService from 'trade-in/services/TradeInService/TradeInService.js';
import VehicleInfo from 'trade-in/components/InstantEstimateWrapper/Steps/VehicleInfo/VehicleInfo.jsx';
import VehicleCondition from 'trade-in/components/InstantEstimateWrapper/Steps/VehicleCondition/VehicleCondition.jsx';
import ContactInformations from 'trade-in/components/InstantEstimateWrapper/Steps/ContactInformations/ContactInformations.jsx';
import VehicleForm from 'trade-in/components/VehicleForm/VehicleForm.jsx';
import VehicleOptions from 'trade-in/components/InstantEstimateWrapper/Steps/VehicleOptions/VehicleOptions.jsx';
import ErrorInspection from 'trade-in/components/InstantEstimateWrapper/Steps/ErrorInspection/ErrorInspection.jsx';
import VehicleAppraisal from 'trade-in/components/InstantEstimateWrapper/Steps/VehicleAppraisal/VehicleAppraisal.jsx';
import AppointmentForm from 'trade-in/components/InstantEstimateWrapper/Steps/AppointmentForm/AppointmentForm.jsx';

import 'trade-in/components/InstantEstimateWrapper/InstantEstimateWrapper.scss';
import TRADE_OPTIONS from '../../constants/tradeOptions.js';
import NewTradeInContext from '../../NewTradeInContext';
import Checkmark from '../../assets/images/Checkmark.jsx';

// The webpackChunkName name here is intended to be public. Webpack will use it to name files in production and development modes.
const CanadianBlackBookEstimate = lazy(() =>
    import(/* webpackChunkName: "CanadianBlackBookEstimate" */ 'trade-in/components/CanadianBlackBookEstimate/CanadianBlackBookEstimate.jsx')
);

class InstantEstimateWrapper extends Component {
    // eslint-disable-next-line react/sort-comp
    static contextType = NewTradeInContext;

    constructor(props) {
        super(props);

        this.state = {
            isScreenLock: false,
            isHeaderShown: true,
            tradeInVehicle: props.tradeInVehicle,
            tradeInValues: null,
            errorInspection: {},
            questionsModel: {},
            account: new Account(),
            index: 0,
            lastIndex: 0,
            inspectionNeeded: false,
        };

        this.logger = new Logger(this.constructor.displayName, ConfigsService.getLoggerContext());

        this.getNewTradeInSteps = this.getNewTradeInSteps.bind(this);
        this.getSteps = this.getSteps.bind(this);
        this.toggleScreenLock = this.toggleScreenLock.bind(this);
        this.updateLead = this.updateLead.bind(this);
        this.updateLeadNew = this.updateLeadNew.bind(this);
        this.updateTradeInVehicle = this.updateTradeInVehicle.bind(this);
        this.onClickStep = this.onClickStep.bind(this);
        this.onDone = this.onDone.bind(this);
        this.onBack = this.onBack.bind(this);
        this.onErrorBack = this.onErrorBack.bind(this);
        this.onError = this.onError.bind(this);
        this.setInspectionNeeded = this.setInspectionNeeded.bind(this);
    }

    componentDidMount() {
        if (this.context?.fieldsLocked) {
            this.context.setFieldsLocked(false);
        }
    }

    onClickStep(i) {
        this.setState({
            index: i,
        });
    }
    onDone() {
        this.setState((prevState) => {
            return { index: prevState.index + 1 };
        });
    }
    onBack() {
        this.setState((prevState) => {
            return { index: prevState.index - 1 };
        });
    }

    onErrorBack() {
        this.setState((prevState) => ({
            isHeaderShown: true,
            index: prevState.lastIndex,
        }));
    }

    onError(questionError, answerError, questionErrorId, currentQuestionsAnswers) {
        this.setState((prevState) => ({
            index: 6, // HARD-CODED- has to be the last step (i.e. Inspection Needed screen)
            lastIndex: prevState.index,
            isHeaderShown: false,
            errorInspection: { questionError, answerError, questionErrorId, currentQuestionsAnswers },
        }));
    }

    onMakeAppointment() {
        this.setState((prevState) => ({
            isHeaderShown: true,
            index: prevState.lastIndex,
        }));
    }

    setInspectionNeeded(tradeInVehicle) {
        this.setState((prevState) => ({
            index: 3, // Contact screen
            lastIndex: prevState.index,
            inspectionNeeded: true,
            tradeInVehicle: { ...prevState.tradeInVehicle, tradeInVehicle },
        }));
    }

    getNewTradeInSteps() {
        const steps = [];

        // Vehicle Selection Step (Screen 1)
        steps.push(() => (
            <VehicleInfo
                autofillTradeInVehicleFromAccount={false}
                account={this.state.account}
                updateAccount={(account) => this.setState({ account })}
                lockScreen={this.toggleScreenLock}
                onFormSubmit={(tradeInVehicle) => this.setState({ tradeInVehicle }, this.onDone)}
                onNextStep={this.onDone}
                onTradeInVehicleUpdated={this.updateTradeInVehicle}
                tradeInVehicle={this.state.tradeInVehicle}
                isRenderedInModal
                useCBB
                quote={this.props.quote}
                displayLabel
            />
        ));

        // Vehicle Option Step (Screen 2)
        steps.push(() => (
            <VehicleOptions
                onFormSubmit={(tradeInVehicle) => this.setState({ tradeInVehicle }, this.onDone)}
                tradeInVehicle={this.state.tradeInVehicle}
                onPreviousStep={this.onBack}
                onNextStep={this.onDone}
            />
        ));
        // Vehicle Condition Step (Screen 3) - Component has to be created
        steps.push(() => (
            <VehicleCondition
                parentModel={this.state.questionsModel}
                parentRawModel={this.state.parentRawModel}
                updateParentModel={(questionsModel, parentRawModel) =>
                    this.setState({ questionsModel, parentRawModel })
                }
                tradeInVehicle={this.state.tradeInVehicle}
                onPreviousStep={this.onBack}
                onNextStep={this.onDone}
                onTradeInVehicleUpdated={this.updateTradeInVehicle}
                onFormSubmit={(tradeInVehicle) => this.setState({ tradeInVehicle }, this.onDone)}
                onError={this.onError}
                questionErrorId={this.state.errorInspection.questionErrorId}
            />
        ));

        // Contact Info Step (Screen 4)
        steps.push(() => (
            <ContactInformations
                tradeInVehicle={this.state.tradeInVehicle}
                onPreviousStep={this.onBack}
                onNextStep={this.onDone}
                onTradeInVehicleUpdated={this.updateTradeInVehicle}
                onFormSubmit={(tradeInVehicle) => this.setState({ tradeInVehicle, isHeaderShown: false }, this.onDone)}
                inspectionNeeded={this.state.inspectionNeeded}
                errorInspection={this.state.errorInspection}
                closeModal={this.props.closeModal}
                quote={this.props.quote}
            />
        ));

        // Estimation Value (Screen 5)
        steps.push(() => (
            <VehicleAppraisal
                tradeInVehicle={this.state.tradeInVehicle}
                onTradeInVehicleUpdated={this.updateTradeInVehicle}
                onPreviousStep={() => {
                    this.setState({ isHeaderShown: true }, this.onBack);
                }}
                onMakeAppointment={() => this.setState({ isHeaderShown: false }, this.onDone)}
                closeModal={this.props.closeModal}
                account={this.state.account}
                handleEstimateChange={this.updateLeadNew}
            />
        ));

        // Appointment Form Step (Screen 6)
        steps.push(() => (
            <AppointmentForm
                tradeInVehicle={this.state.tradeInVehicle}
                quote={this.props.quote}
                closeModal={this.props.closeModal}
            />
        ));

        // Error: inspection needed (Screen 7 - temporary)
        steps.push(() => (
            <ErrorInspection
                tradeInVehicle={this.state.tradeInVehicle}
                error={this.state.errorInspection}
                onPreviousStep={this.onErrorBack}
                onNextStep={this.setInspectionNeeded}
            />
        ));

        return steps;
    }

    // getSteps - Code with the current tradeIn logic
    // TODO: newTradeIn - Remove getSteps after deploy new tradeIn to the PROD
    getSteps() {
        const steps = [];
        steps.push(({ goNextStep }) => (
            <VehicleForm
                autofillTradeInVehicleFromAccount={false}
                account={this.state.account}
                updateAccount={(account) => this.setState({ account })}
                lockScreen={this.toggleScreenLock}
                onFormSubmit={(data) => this.setState(data, goNextStep)}
                onTradeInVehicleUpdated={this.updateTradeInVehicle}
                tradeInVehicle={this.state.tradeInVehicle}
                isRenderedInModal
                useCBB
                quote={this.props.quote}
            />
        ));

        steps.push(({ goPreviousStep }) => (
            <NoSSR>
                <Suspense fallback="">
                    <CanadianBlackBookEstimate
                        account={this.state.account}
                        tradeInVehicle={this.state.tradeInVehicle}
                        backToVehicleForm={goPreviousStep}
                        tradeInValues={this.state.tradeInValues}
                        handleEstimateChange={this.updateLead}
                        isOfficialTradeInActivated={this.props.isShowroomV2Enabled}
                        isShowroomV2Enabled={this.props.isShowroomV2Enabled}
                        isRenderedInModal
                    />
                </Suspense>
            </NoSSR>
        ));

        return steps;
    }

    toggleScreenLock() {
        this.setState((prevState) => {
            return { isScreenLock: !prevState.isScreenLock };
        });
    }

    updateTradeInVehicle(tradeInVehicle, onStateSet = noop) {
        this.setState((oldState) => {
            return { ...oldState, tradeInVehicle };
        }, onStateSet);
    }

    updateLead(amount, profile) {
        const { tradeInValues, tradeInVehicle, account } = this.state;

        const updatedTradeInModel = new TradeInModel({
            tradeInVehicle,
            estimation: {
                amount,
                amountWasUpdated: true,
                high: tradeInValues.high,
                low: tradeInValues.low,
            },
            profile: profile || account,
        });

        return TradeInService.saveTradeIn(updatedTradeInModel, this.props.quote);
    }

    // The same as updateLead but for new tradeIn wizard
    // Change or remove once the old tradeIn depreciated
    async updateLeadNew(amount, selectedOption, existingAccount) {
        const { tradeInVehicle } = this.state;
        let accountObject = {};
        try {
            const account = await ProfileService.fetchProfile();
            if (account) {
                accountObject = {
                    firstname: account?.firstname,
                    lastname: account?.lastname,
                    email: account?.email1,
                    homePhone: account?.homePhone,
                    optIn: account?.optInEmail,
                };
            }
        } catch (e) {
            this.logger.error('Error fetching the profile information', e);
        }

        const tradeOptions = {
            1: `${LocaleService.t('TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.TRADE_ON_PURCHASE.TITLE')}`,
            2: `${LocaleService.t('TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.SELL_VEHICLE.TITLE')}`,
            3: `${LocaleService.t('TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.NOT_SURE.TITLE')}`,
        };

        const updatedTradeInModel = new TradeInModelNew({
            tradeInVehicle,
            estimation: {
                amount,
                amountWasUpdated: true,
                high: amount,
                low: amount,
            },
            info:
                LocaleService.t('TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.SELECTED_TRADE_OPTION', {
                    option: tradeOptions[selectedOption],
                }) +
                // Specify that a email cheque has been sent to client
                (selectedOption === TRADE_OPTIONS.SELL_VEHICLE
                    ? LocaleService.t('TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.SELL_VEHICLE_EMAIL_CHEQUE_LEAD')
                    : ''),
            profile: existingAccount ? accountObject : tradeInVehicle.contact,
        });

        return TradeInService.saveTradeInNew(updatedTradeInModel, this.props.quote);
    }

    render() {
        const VehicleInfoLabel = () => (
            <div className="step--withIcon">
                <icon className="icon-cbb-icons-vehicle-information" />
                <span>{LocaleService.t('TRADE_IN.SECTIONS.VEHICLE_INFO')}</span>
            </div>
        );
        const VehicleOptionsLabel = () => (
            <div className="step--withIcon">
                <icon className="icon-cbb-icons-vehicle-options" />
                <span>{LocaleService.t('TRADE_IN.SECTIONS.VEHICLE_OPTIONS')}</span>
            </div>
        );
        const VehicleConditionLabel = () => (
            <div className="step--withIcon">
                <icon className="icon-cbb-icons-vehicle-condition" />
                <span>{LocaleService.t('TRADE_IN.SECTIONS.VEHICLE_CONDITION')}</span>
            </div>
        );
        const ContactInfoLabel = () => (
            <div className="step--withIcon">
                <icon className="icon-cbb-icons-contact" />
                <span>{LocaleService.t('TRADE_IN.SECTIONS.CONTACT_INFO')}</span>
            </div>
        );
        const tradeInNavigationSteps = [
            { label: <VehicleInfoLabel />, name: LocaleService.t('TRADE_IN.SECTIONS.VEHICLE_INFO') },
            { label: <VehicleOptionsLabel />, name: LocaleService.t('TRADE_IN.SECTIONS.VEHICLE_OPTIONS') },
            { label: <VehicleConditionLabel />, name: LocaleService.t('TRADE_IN.SECTIONS.VEHICLE_CONDITION') },
            { label: <ContactInfoLabel />, name: LocaleService.t('TRADE_IN.SECTIONS.CONTACT_INFO') },
        ];
        const progressMargin = 100 / tradeInNavigationSteps.length / 2;
        const newTradeInSteps = this.context.displayNewTradeIn && this.getNewTradeInSteps();

        return (
            <div
                className={`TradeIn__wrapper${this.state.index === 4 ? ' appraisal__wrapper' : ''}`}
                id="webauto-trade-in-modal"
            >
                <Checkmark />
                <LoaderFullScreen show={this.state.isScreenLock} message={LocaleService.t('TRADE_IN.SCREEN_LOCKED')}>
                    {/* TODO: newTradeIn - Remove displayNewTradeIn condition after deploy new tradeIn to the PROD */}
                    {this.context.displayNewTradeIn ? (
                        <>
                            <div className={`steps-header header-${this.state.isHeaderShown ? 'shown' : 'hidden'}`}>
                                <h4>{LocaleService.t('TRADE_IN.MODAL_TITLE')}</h4>
                                <StepBar
                                    currentStep={this.state.index || 0}
                                    onClick={this.onClickStep}
                                    steps={tradeInNavigationSteps}
                                />
                                <div style={{ margin: `0 ${progressMargin}%` }}>
                                    <ProgressBar
                                        max={tradeInNavigationSteps.length - 1}
                                        min={0}
                                        amount={this.state.index || 0}
                                    />
                                </div>
                                {this.state.index < tradeInNavigationSteps.length && (
                                    <div className="steps-header--bottom">
                                        <span className="steps-header--bottom--currentStep">
                                            {tradeInNavigationSteps?.[this.state.index]?.name}
                                        </span>
                                        <span className="steps-header--bottom--progress">
                                            {`${this.state.index + 1}/${tradeInNavigationSteps.length}`}
                                        </span>
                                    </div>
                                )}
                            </div>
                            <Wizard
                                steps={newTradeInSteps}
                                parentSelector=".TradeIn__wrapper"
                                startingStepIndex={this.state.index || 0}
                            />
                            <button
                                type="button"
                                className="uk-button uk-button--close"
                                id="close-modal-button"
                                onClick={this.props.closeModal}
                            >
                                <span aria-hidden="true">×</span>
                            </button>
                        </>
                    ) : (
                        <Wizard steps={this.getSteps()} parentSelector=".TradeIn__wrapper" />
                    )}
                </LoaderFullScreen>
            </div>
        );
    }
}

InstantEstimateWrapper.propTypes = {
    tradeInVehicle: PropTypes.instanceOf(Vehicle.TradeInVehicleModel).isRequired,
    isShowroomV2Enabled: PropTypes.bool.isRequired,
    quote: PropTypes.instanceOf(Quote.QuoteModel).isRequired,
};

InstantEstimateWrapper.displayName = 'InstantEstimateWrapper';

export default InstantEstimateWrapper;
